import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { DiscountRequest } from "../../entry/server/app.ts";
import { appRepo } from "../../repo/app-repo.ts";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { AppContext } from "../../context/app-context.tsx";
import { toTehranDateTimeString } from "../../utils/common_helpers.ts";

export default function NewDiscount() {
  const navigate = useNavigate();
  const { setMessage } = useContext(AppContext);

  const [form, setForm] = useState({
    code: "",
    discount_percentage: "",
    max_uses: "",
    plans_id: "",
    is_active: true,
  });
  const [expiresAt, setExpiresAt] = useState<DateObject | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFormValid = useMemo(() => {
    return Boolean(
      form.code.trim() &&
      form.discount_percentage.trim() &&
      form.max_uses.trim() &&
      Boolean(expiresAt)
    );
  }, [expiresAt, form]);

  const onChangeField = (
    field: "code" | "discount_percentage" | "max_uses" | "plans_id",
    value: string
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isFormValid) {
      setMessage({ type: "error", message: "لطفا همه فیلدها را کامل کنید." });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: DiscountRequest = {
        code: form.code.trim(),
        discount_percentage: Number(form.discount_percentage),
        max_uses: Number(form.max_uses),
        expires_at: toTehranDateTimeString(expiresAt),
        plans_id: form.plans_id.trim() ? form.plans_id.split(",").map(Number) : [],
        is_active: form.is_active,
      };

      const response = await appRepo.createDiscount(payload);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "ایجاد کد تخفیف با خطا مواجه شد." });
        return;
      }

      setMessage({ type: "success", message: "کد تخفیف با موفقیت ایجاد شد." });
      navigate("/admin/discount-codes");
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. لطفا دوباره تلاش کنید." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="space-y-4">
      <AdminPageTitle title="ایجاد کد تخفیف" />
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2">
            <span className="text-sm text-slate-300">کد تخفیف</span>
            <input
              value={form.code}
              onChange={(event) => onChangeField("code", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
              placeholder="OFF20"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">درصد تخفیف</span>
            <input
              type="number"
              min="1"
              max="100"
              value={form.discount_percentage}
              onChange={(event) => onChangeField("discount_percentage", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
              placeholder="20"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">حداکثر استفاده</span>
            <input
              type="number"
              min="0"
              value={form.max_uses}
              onChange={(event) => onChangeField("max_uses", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
              placeholder="100"
            />
          </label>

          <label className="space-y-2 flex flex-col">
            <span className="text-sm text-slate-300">تاریخ انقضا</span>
            <DatePicker
              value={expiresAt}
              onChange={(value) => setExpiresAt((value as DateObject) || null)}
              calendar={persian}
              locale={persian_fa}
              format="YYYY/MM/DD HH:mm"
              calendarPosition="bottom-right"
              plugins={[<TimePicker key="time-picker" position="bottom" />]}
              inputClass="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
            />
          </label>

          <label className="space-y-2 md:col-span-2">
            <span className="text-sm text-slate-300">شناسه پلن‌ها (با کاما جدا کنید)</span>
            <input
              value={form.plans_id}
              onChange={(event) => onChangeField("plans_id", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
              placeholder="1,2,3"
            />
          </label>

          <label className="flex items-center gap-2 md:col-span-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
              className="h-4 w-4 accent-indigo-500"
            />
            <span className="text-sm text-slate-300">کد تخفیف فعال باشد</span>
          </label>

          <div className="flex items-center gap-3 md:col-span-2">
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "در حال ایجاد..." : "ایجاد کد تخفیف"}
            </button>
            <Link
              to="/admin/discount-codes"
              className="inline-flex rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
            >
              انصراف
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
}
